/**
 * Security Audits Page — F010
 *
 * Three-tab layout: Dashboard | Audits | Offshore Staff
 *
 * Key divergences from spec (PATTERN-DB-007):
 *  - auditType: internal | external | regulatory | certification (NOT cyber_insurance etc.)
 *  - auditStatus: planned | in_progress | completed | cancelled (NOT draft/signed_off)
 *  - No isActive boolean — active = endDate IS NULL or in future
 *  - Offshore staff: providerCompany (NOT agencyName), providerCountry (NOT location)
 *  - No deviceCompliant/accessCompliant — schema has confidentialitySigned/privacyTrainingCompleted
 *  - Dashboard: GET /api/compliance/security-dashboard
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  Plus,
  X,
  Pencil,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  MinusCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useUrlState } from "@/hooks/use-url-state";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ComplianceLayout } from "./compliance-layout";
import { SecurityAuditModal } from "@/components/modals/security-audit-modal";
import { OffshoreStaffModal, type OffshoreStaffData } from "@/components/modals/offshore-staff-modal";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SecurityAudit {
  id: string;
  auditType: string;
  auditName: string;
  auditStatus: string;
  plannedDate: string | null;
  startDate: string | null;
  completionDate: string | null;
  createdAt: string;
  itemCount: number;
  compliantCount: number;
  nonCompliantCount: number;
}

interface OffshoreStaff {
  id: string;
  staffName: string;
  staffRole: string | null;
  staffEmail: string | null;
  providerCompany: string;
  providerCountry: string;
  accessLevel: string;
  complianceStatus: string;
  confidentialitySigned: boolean | null;
  confidentialityDate: string | null;
  privacyTrainingCompleted: boolean | null;
  privacyTrainingDate: string | null;
  startDate: string;
  endDate: string | null;
  notes: string | null;
}

interface DashboardLatestAudit {
  auditType: string;
  auditName: string;
  status: string;
  compliancePercentage: number;
  lastCompleted: string | null;
}

interface DashboardData {
  latestAudits: DashboardLatestAudit[];
  offshoreStaffSummary: {
    totalActive: number;
    fullyCompliant: number;
    partiallyCompliant: number;
    nonCompliant: number;
  };
  overdueRemediations: number;
  upcomingVerifications: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUDIT_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  internal: { label: "Internal", className: "bg-blue-100 text-blue-700 border-blue-300" },
  external: { label: "External", className: "bg-purple-100 text-purple-700 border-purple-300" },
  regulatory: { label: "Regulatory", className: "bg-amber-100 text-amber-700 border-amber-300" },
  certification: { label: "Certification", className: "bg-green-100 text-green-700 border-green-300" },
};

const AUDIT_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  planned: { label: "Planned", className: "bg-gray-100 text-gray-700 border-gray-300" },
  in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-700 border-blue-300" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700 border-green-300" },
  cancelled: { label: "Cancelled", className: "bg-red-100 text-red-700 border-red-300" },
};

const COMPLIANCE_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  compliant: { label: "Compliant", className: "bg-green-100 text-green-700 border-green-300" },
  non_compliant: { label: "Non-Compliant", className: "bg-red-100 text-red-700 border-red-300" },
  partially_compliant: { label: "Partial", className: "bg-amber-100 text-amber-700 border-amber-300" },
  not_applicable: { label: "N/A", className: "bg-gray-100 text-gray-600 border-gray-300" },
};

const AUDIT_TYPE_OPTIONS = [
  { value: "none", label: "All Types" },
  { value: "internal", label: "Internal" },
  { value: "external", label: "External" },
  { value: "regulatory", label: "Regulatory" },
  { value: "certification", label: "Certification" },
];

const AUDIT_STATUS_OPTIONS = [
  { value: "none", label: "All Statuses" },
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: "none", label: "All Staff" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

function compliancePct(audit: SecurityAudit): number {
  if (audit.itemCount === 0) return 0;
  return Math.round((audit.compliantCount / audit.itemCount) * 100);
}

function pctColourClass(pct: number): string {
  if (pct >= 80) return "text-green-700";
  if (pct >= 50) return "text-amber-600";
  return "text-red-600";
}

function pctBarClass(pct: number): string {
  if (pct >= 80) return "bg-green-500";
  if (pct >= 50) return "bg-amber-500";
  return "bg-red-500";
}

// ---------------------------------------------------------------------------
// Dashboard Tab
// ---------------------------------------------------------------------------

interface DashboardTabProps {
  onCreateAudit: () => void;
}

function DashboardTab({ onCreateAudit }: DashboardTabProps) {
  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ["/api/compliance/security-dashboard"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/compliance/security-dashboard");
      if (!response.ok) throw new Error("Failed to load dashboard");
      return response.json();
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-5">
              <div className="h-16 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!data || (data.latestAudits.length === 0 && data.offshoreStaffSummary.totalActive === 0)) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <ShieldCheck className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <p className="mt-3 font-medium text-muted-foreground">
            No security audits conducted yet
          </p>
          <Button className="mt-4" onClick={onCreateAudit} data-testid="button-create-first-audit">
            <Plus className="mr-2 h-4 w-4" />
            Create your first audit
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Audit type traffic light cards */}
      {data.latestAudits.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Latest Audit by Type
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {data.latestAudits.map((a) => {
              const pct = a.compliancePercentage;
              const typeCfg =
                AUDIT_TYPE_CONFIG[a.auditType] ?? {
                  label: a.auditType,
                  className: "bg-gray-100 text-gray-700 border-gray-300",
                };
              const statusCfg =
                AUDIT_STATUS_CONFIG[a.status] ?? {
                  label: a.status,
                  className: "bg-gray-100 text-gray-700 border-gray-300",
                };

              return (
                <Card
                  key={a.auditType}
                  data-testid={`audit-card-${a.auditType}`}
                  className="overflow-hidden"
                >
                  <CardContent className="p-0">
                    {/* Colour bar */}
                    <div
                      className={cn(
                        "h-1.5",
                        pct >= 80
                          ? "bg-green-500"
                          : pct >= 50
                          ? "bg-amber-500"
                          : "bg-red-500"
                      )}
                    />
                    <div className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className={cn("text-xs", typeCfg.className)}>
                          {typeCfg.label}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={cn("text-xs", statusCfg.className)}
                        >
                          {statusCfg.label}
                        </Badge>
                      </div>
                      <p className="text-xs font-medium truncate" title={a.auditName}>
                        {a.auditName}
                      </p>
                      <div className="flex items-end justify-between">
                        <div>
                          <div
                            className={cn("text-2xl font-bold", pctColourClass(pct))}
                            data-testid={`dashboard-pct-${a.auditType}`}
                          >
                            {pct}%
                          </div>
                          <div className="text-xs text-muted-foreground">Compliance</div>
                        </div>
                        {a.lastCompleted && (
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">Completed</div>
                            <div className="text-xs font-medium">
                              {formatDate(a.lastCompleted)}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Progress bar */}
                      <div className="h-1.5 w-full rounded-full bg-muted">
                        <div
                          className={cn("h-1.5 rounded-full", pctBarClass(pct))}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {/* Overdue remediations */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <div>
                <div
                  className="text-2xl font-bold text-red-600"
                  data-testid="dashboard-overdue-count"
                >
                  {data.overdueRemediations}
                </div>
                <div className="text-xs text-muted-foreground">Overdue Remediations</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming verifications */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-500" />
              <div>
                <div
                  className="text-2xl font-bold"
                  data-testid="dashboard-upcoming-count"
                >
                  {data.upcomingVerifications}
                </div>
                <div className="text-xs text-muted-foreground">Upcoming (30 days)</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active offshore staff */}
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold" data-testid="dashboard-staff-total">
              {data.offshoreStaffSummary.totalActive}
            </div>
            <div className="text-xs text-muted-foreground">Active Offshore Staff</div>
          </CardContent>
        </Card>

        {/* Staff compliance */}
        <Card>
          <CardContent className="p-4 space-y-1">
            <div className="text-xs font-medium text-muted-foreground uppercase">
              Staff Compliance
            </div>
            <div className="flex gap-2 flex-wrap">
              <span
                className="flex items-center gap-1 text-xs text-green-700"
                data-testid="dashboard-staff-compliant"
              >
                <CheckCircle2 className="h-3 w-3" />
                {data.offshoreStaffSummary.fullyCompliant} Compliant
              </span>
              <span
                className="flex items-center gap-1 text-xs text-amber-600"
                data-testid="dashboard-staff-partial"
              >
                <MinusCircle className="h-3 w-3" />
                {data.offshoreStaffSummary.partiallyCompliant} Partial
              </span>
              <span
                className="flex items-center gap-1 text-xs text-red-600"
                data-testid="dashboard-staff-noncompliant"
              >
                <XCircle className="h-3 w-3" />
                {data.offshoreStaffSummary.nonCompliant} Non-Compliant
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audits Tab
// ---------------------------------------------------------------------------

interface AuditsTabProps {
  onCreateAudit: () => void;
}

function AuditsTab({ onCreateAudit }: AuditsTabProps) {
  const [, setLocation] = useLocation();
  const [auditType, setAuditType] = useUrlState("auditType", "none");
  const [auditStatus, setAuditStatus] = useUrlState("auditStatus", "none");

  const queryParams = new URLSearchParams();
  if (auditType !== "none") queryParams.set("auditType", auditType);
  if (auditStatus !== "none") queryParams.set("status", auditStatus);

  const { data, isLoading } = useQuery<SecurityAudit[]>({
    queryKey: ["/api/compliance/security-audits", auditType, auditStatus],
    queryFn: async () => {
      const url = `/api/compliance/security-audits${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await apiRequest("GET", url);
      if (!response.ok) throw new Error("Failed to load audits");
      return response.json();
    },
  });

  const audits = Array.isArray(data) ? data : [];
  const hasActiveFilters = auditType !== "none" || auditStatus !== "none";

  const clearFilters = () => {
    setAuditType("none");
    setAuditStatus("none");
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Security Audits</h2>
          <p className="text-sm text-muted-foreground">
            Internal, external, regulatory, and certification audits
          </p>
        </div>
        <Button
          data-testid="button-create-audit"
          onClick={onCreateAudit}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Audit
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-3 items-center">
          <Select value={auditType} onValueChange={setAuditType}>
            <SelectTrigger className="w-[180px]" data-testid="select-filter-audit-type">
              <SelectValue placeholder="Audit Type" />
            </SelectTrigger>
            <SelectContent>
              {AUDIT_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={auditStatus} onValueChange={setAuditStatus}>
            <SelectTrigger className="w-[160px]" data-testid="select-filter-audit-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {AUDIT_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground"
            >
              <X className="mr-1 h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>
        {hasActiveFilters && (
          <p className="text-xs text-muted-foreground mt-2">
            Showing {audits.length} audit{audits.length !== 1 ? "s" : ""}
          </p>
        )}
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Audit Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-32">Compliance</TableHead>
              <TableHead className="w-20 text-right">Items</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : audits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  <div className="space-y-2">
                    <p>No security audits found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-create-audit-empty"
                      onClick={onCreateAudit}
                    >
                      Create Audit
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              audits.map((audit) => {
                const pct = compliancePct(audit);
                const typeCfg =
                  AUDIT_TYPE_CONFIG[audit.auditType] ?? {
                    label: audit.auditType,
                    className: "bg-gray-100 text-gray-700 border-gray-300",
                  };
                const statusCfg =
                  AUDIT_STATUS_CONFIG[audit.auditStatus] ?? {
                    label: audit.auditStatus,
                    className: "bg-gray-100 text-gray-700 border-gray-300",
                  };

                return (
                  <TableRow
                    key={audit.id}
                    data-testid={`row-audit-${audit.id}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      setLocation(`/compliance/security-audits/${audit.id}`)
                    }
                  >
                    <TableCell className="font-medium">{audit.auditName}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={typeCfg.className}>
                        {typeCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusCfg.className}>
                        {statusCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {audit.itemCount > 0 ? (
                        <div className="space-y-1">
                          <div
                            className={cn("text-sm font-medium", pctColourClass(pct))}
                            data-testid={`compliance-pct-${audit.id}`}
                          >
                            {pct}%
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-muted">
                            <div
                              className={cn("h-1.5 rounded-full", pctBarClass(pct))}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">No items</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {audit.itemCount > 0 ? (
                        <span>
                          <span className="text-green-700">{audit.compliantCount}</span>
                          <span className="text-muted-foreground">/{audit.itemCount}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {formatDate(
                        audit.completionDate ?? audit.startDate ?? audit.plannedDate
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Offshore Staff Tab
// ---------------------------------------------------------------------------

function OffshoreStaffTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<OffshoreStaffData | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [activeFilter, setActiveFilter] = useUrlState("staffActive", "none");

  const queryParams = new URLSearchParams();
  if (activeFilter !== "none") queryParams.set("active", activeFilter);

  const { data, isLoading } = useQuery<OffshoreStaff[]>({
    queryKey: ["/api/compliance/offshore-staff", activeFilter],
    queryFn: async () => {
      const url = `/api/compliance/offshore-staff${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await apiRequest("GET", url);
      if (!response.ok) throw new Error("Failed to load offshore staff");
      return response.json();
    },
  });

  const staffList = Array.isArray(data) ? data : [];

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/compliance/offshore-staff/${id}`
      );
      if (!response.ok && response.status !== 204) {
        throw new Error("Failed to delete staff record");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/offshore-staff"] });
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/security-dashboard"] });
      toast({ title: "Staff record deleted" });
      setDeletingId(null);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
      setDeletingId(null);
    },
  });

  const handleEdit = (staff: OffshoreStaff) => {
    setEditingStaff(staff);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingStaff(null);
    setIsModalOpen(true);
  };

  const isStaffActive = (staff: OffshoreStaff): boolean => {
    if (!staff.endDate) return true;
    const today = new Date().toISOString().split("T")[0];
    return staff.endDate > today;
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Offshore Staff</h2>
          <p className="text-sm text-muted-foreground">
            Track offshore and third-party staff with data access
          </p>
        </div>
        <Button data-testid="button-add-staff" onClick={handleAddNew}>
          <Plus className="mr-2 h-4 w-4" />
          Add Staff
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex gap-3 items-center">
          <Select value={activeFilter} onValueChange={setActiveFilter}>
            <SelectTrigger className="w-[150px]" data-testid="select-filter-staff-active">
              <SelectValue placeholder="Active Status" />
            </SelectTrigger>
            <SelectContent>
              {ACTIVE_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {activeFilter !== "none" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveFilter("none")}
              className="text-muted-foreground"
            >
              <X className="mr-1 h-3 w-3" />
              Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Confidentiality</TableHead>
              <TableHead>Privacy Training</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : staffList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="py-12 text-center text-muted-foreground">
                  <div className="space-y-2">
                    <p>No offshore staff tracked</p>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-add-staff-empty"
                      onClick={handleAddNew}
                    >
                      Add Staff Member
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              staffList.map((staff) => {
                const compCfg =
                  COMPLIANCE_STATUS_CONFIG[staff.complianceStatus] ?? {
                    label: staff.complianceStatus,
                    className: "bg-gray-100 text-gray-600 border-gray-300",
                  };
                const active = isStaffActive(staff);

                return (
                  <TableRow
                    key={staff.id}
                    data-testid={`row-staff-${staff.id}`}
                    className="hover:bg-muted/30"
                  >
                    <TableCell className="font-medium">
                      <div>{staff.staffName}</div>
                      {staff.staffEmail && (
                        <div className="text-xs text-muted-foreground">
                          {staff.staffEmail}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{staff.providerCompany}</TableCell>
                    <TableCell>{staff.providerCountry}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {staff.staffRole ?? "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={compCfg.className}>
                        {compCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {staff.confidentialitySigned ? (
                        <span className="flex items-center gap-1 text-xs text-green-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Signed
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-red-600">
                          <XCircle className="h-3.5 w-3.5" />
                          Not signed
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {staff.privacyTrainingCompleted ? (
                        <span className="flex items-center gap-1 text-xs text-green-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Completed
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-red-600">
                          <XCircle className="h-3.5 w-3.5" />
                          Incomplete
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {active ? (
                        <Badge
                          variant="outline"
                          className="bg-green-100 text-green-700 border-green-300"
                          data-testid={`staff-active-badge-${staff.id}`}
                        >
                          Active
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-gray-100 text-gray-600 border-gray-300"
                          data-testid={`staff-inactive-badge-${staff.id}`}
                        >
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          data-testid={`button-edit-staff-${staff.id}`}
                          onClick={() => handleEdit(staff)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          data-testid={`button-delete-staff-${staff.id}`}
                          onClick={() => setDeletingId(staff.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Offshore Staff Modal */}
      <OffshoreStaffModal
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) setEditingStaff(null);
        }}
        editingStaff={editingStaff}
        onSuccess={() => {
          setEditingStaff(null);
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={deletingId !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingId(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-delete-staff-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Staff Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this offshore staff record? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-staff"
              onClick={() => {
                if (deletingId) deleteMutation.mutate(deletingId);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

function SecurityAuditsContent() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isCreateAuditOpen, setIsCreateAuditOpen] = useState(false);
  const [, setLocation] = useLocation();

  const handleCreateAudit = () => {
    setIsCreateAuditOpen(true);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold">Data & Cyber Security</h1>
        <p className="text-sm text-muted-foreground">
          Security audit management, compliance tracking, and offshore staff registry
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-security-dashboard">
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="audits" data-testid="tab-security-audits">
            Audits
          </TabsTrigger>
          <TabsTrigger value="offshore-staff" data-testid="tab-offshore-staff">
            Offshore Staff
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <DashboardTab onCreateAudit={handleCreateAudit} />
        </TabsContent>

        <TabsContent value="audits" className="mt-4">
          <AuditsTab onCreateAudit={handleCreateAudit} />
        </TabsContent>

        <TabsContent value="offshore-staff" className="mt-4">
          <OffshoreStaffTab />
        </TabsContent>
      </Tabs>

      {/* Create Audit Modal */}
      <SecurityAuditModal
        open={isCreateAuditOpen}
        onOpenChange={setIsCreateAuditOpen}
        onSuccess={(id) => {
          setIsCreateAuditOpen(false);
          setLocation(`/compliance/security-audits/${id}`);
        }}
      />
    </div>
  );
}

export default function SecurityAuditsPage() {
  return (
    <ComplianceLayout>
      <SecurityAuditsContent />
    </ComplianceLayout>
  );
}
